import React from 'react';
import { connect } from 'react-redux';
import pt from 'prop-types';

import auth from 'services/auth';
import { getPartnerIdFromPath } from 'utils/container';
import LoginLayout from 'components/login/layout';
import LoginPage from 'components/login/login';
import RecoverPage from 'components/login/recover';
import LoadingView from 'components/view/loading';
import NotFoundPage from 'components/pages/error/404';

import actions from 'partners/actions';
import JoinPage from 'partners/components/pages/login/join';

const mapStateToProps = (state) => ({
  flash: state.flash,
  invited: state.user.invited || state.partner.invited,
});

const mapDispatchToProps = (dispatch) => ({
  performLogin: (username, password, partnerId) => {
    return dispatch(actions.user.performLogin(username, password, partnerId));
  },

  fetchInvitedUser: (key) => {
    return dispatch(actions.user.fetchInvited(key));
  },

  updateInvitedUser: (key, values) => {
    return dispatch(actions.user.updateInvited(key, values));
  },

  fetchInvitedPartner: (id) => {
    return dispatch(actions.partner.fetchInvited(id));
  },

  createPartner: (id) => {
    return dispatch(actions.partner.create(id));
  },

  putSendgridContact: (query) => {
    return dispatch(actions.user.putSendgridContact(query));
  },

  updateInvitedPartner: (id, values) => {
    return dispatch(actions.partner.updateInvited(id, values));
  },

  recover: (data) => {
    return dispatch(actions.user.recover(data));
  },

  flashSuccess: (message) => {
    dispatch(actions.flash.success(message));
  },

  flashError: (message) => {
    dispatch(actions.flash.error(message));
  },
});

export class Login extends React.PureComponent {
  static propTypes = {
    params: pt.object,
    router: pt.object,
    route: pt.object,
    performLogin: pt.func,
    fetchInvitedPartner: pt.func,
    updateInvitedPartner: pt.func,
    createPartner: pt.func,
    putSendgridContact: pt.func,
    fetchInvitedUser: pt.func,
    updateInvitedUser: pt.func,
    recover: pt.func,
    flashSuccess: pt.func,
    flashError: pt.func,
  };

  static onEnter() {
    window.scrollTo(0, 0);
  }

  static onChange() {
    window.scrollTo(0, 0);
  }

  constructor(props, context) {
    super(props, context);

    this.state = {
      loaded: false,
      notfound: false,
      defaultValues: {},
      onSubmitLogin: this.onSubmitLogin.bind(this),
      onSubmitJoin: this.onSubmitJoin.bind(this),
      onSubmitRecover: this.onSubmitRecover.bind(this),
    };
  }

  async componentDidMount() {
    const { route } = this.props;

    switch (route.page) {
      case 'join':
        await this.handleJoin();
        break;
      case 'login':
      case 'recover':
        this.setState({ loaded: true });
        break;
      default:
        this.setState({ notfound: true });
    }
  }

  async handleJoin() {
    const { params, fetchInvitedPartner, fetchInvitedUser } = this.props;
    const { partnerId, key } = params;
    // Both paths carry a key; the partner prefix is what tells them apart
    const result = partnerId
      ? await fetchInvitedUser(key)
      : await fetchInvitedPartner(key);

    if (!result) {
      this.setState({ notfound: true, loaded: true });
      return;
    }

    this.setState({ loaded: true });
  }

  onSubmitLogin({ username, password }) {
    const { params, router, flashError, performLogin } = this.props;

    return performLogin(username, password, params.partnerId).then((user) => {
      const { to } = router.location.query;

      if (user === null) {
        flashError('Your email or password is incorrect, please try again.');
        return false;
      } else if (user.error) {
        const toPath = decodeURIComponent(to || '');

        // Logging in to accept a store's partner invite: its page offers to
        // create a partner dashboard first
        if (
          user.error.code === 'no_partner_account' &&
          toPath.startsWith('/accept-store/')
        ) {
          router.originalReplace(`${toPath}?no_partner=1`);
          return false;
        }

        flashError(user.error.message || 'An unknown error occurred');
        return false;
      }

      // A redirect target for another partner would produce a mangled url
      // once the router prefixes it; land on the session's partner instead
      const toPartnerId = getPartnerIdFromPath(decodeURIComponent(to || ''));
      const redirectTo =
        toPartnerId && toPartnerId !== user.partner_id
          ? `/${user.partner_id}`
          : to;

      auth.redirectPartnerSessionTo(redirectTo, router);
    });
  }

  onSubmitJoin(values) {
    const { router, params } = this.props;
    const { partnerId, key } = params;

    if (!key) {
      router.replace('/login');
      this.setState({ notfound: true });
      return false;
    }

    if (partnerId) {
      return this.onJoinTeam(partnerId, key, values);
    }

    return this.onCompleteSignup(key, values);
  }

  async onJoinTeam(partnerId, key, values) {
    const { router, flashError, updateInvitedUser, performLogin } = this.props;
    const result = await updateInvitedUser(key, values);

    if (!result) {
      return false;
    }

    if (result.errors) {
      flashError(
        result.errors.username
          ? 'Looks like you already have an account, please log in'
          : result.errors,
      );

      return false;
    }

    await performLogin(values.email, values.password, partnerId);

    router.replace(`/${partnerId}`);
  }

  async onCompleteSignup(key, values) {
    const {
      router,
      flashError,
      updateInvitedPartner,
      createPartner,
      putSendgridContact,
    } = this.props;
    const lead = await updateInvitedPartner(key, values);

    if (!lead) {
      return false;
    }

    if (lead.errors) {
      flashError(
        lead.errors.username
          ? 'Looks like you already have an account, please log in'
          : lead.errors,
      );

      return false;
    }

    const partner = await createPartner(lead.id);

    if (!partner) {
      return false;
    }

    if (partner.error || partner.errors) {
      flashError(
        partner.error || 'An unknown error occurred, please try again',
      );

      return false;
    }

    await putSendgridContact({
      email: values.email,
      name: partner.name,
      signed_up: true,
    });

    router.replace(`/${partner.id}`);
  }

  async onSubmitRecover({ email, password }) {
    const { router, params, flashError, flashSuccess, recover } = this.props;
    const { resetKey } = params;

    const result = await recover({ email, password, resetKey });

    if (result.success) {
      if (resetKey) {
        router.replace('/login');
        flashSuccess('Your password has been reset, login to continue');
      } else {
        flashSuccess('An email has been sent to recover your password');
      }
    } else if (result.errors?.reset_key) {
      // Unknown and expired keys both come back as `errors.reset_key`
      router.replace('/recover');
      flashError(
        'This reset link is invalid or has expired. Please request a new one.',
      );
    } else if (result.error) {
      flashError(result.error.message || result.error);
    } else if (result.errors) {
      flashError(result.errors);
    }
  }

  renderPage() {
    switch (this.props.route.page) {
      case 'join':
        return <JoinPage {...this.props} {...this.state} />;
      case 'recover':
        return <RecoverPage {...this.props} {...this.state} isPartnerLogin />;
      default:
        return (
          <LoginPage
            {...this.props}
            {...this.state}
            isPartnerLogin
            partnerId={this.props.params.partnerId}
          />
        );
    }
  }

  render() {
    if (!this.state.loaded) {
      return <LoadingView />;
    }

    if (this.state.notfound) {
      return <NotFoundPage />;
    }

    return (
      <LoginLayout
        {...this.props}
        {...this.state}
        className="login-partner-container"
      >
        {this.renderPage()}
      </LoginLayout>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Login);
